import React, { useMemo } from "react";
import { Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";

export enum ProjectConfigTab {
  STATUSES = "statuses",
}

export interface ProjectConfigTabConfig {
  id: ProjectConfigTab;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface ProjectConfigNavProps {
  activeTab: ProjectConfigTab;
  onTabChange: (tab: ProjectConfigTab) => void;
}

const PROJECT_CONFIG_TAB_CONFIG: ProjectConfigTabConfig[] = [
  { id: ProjectConfigTab.STATUSES, name: "Statuses", icon: Settings },
];

const ProjectConfigNav = React.memo(
  ({ activeTab, onTabChange }: ProjectConfigNavProps) => {
    const memoizedTabs = useMemo(() => PROJECT_CONFIG_TAB_CONFIG, []);

    return (
      <div className="flex w-64 pt-4 flex-col pb-2 h-full border-r border-secondary-n-900">
        <div className="flex justify-between items-center pr-2 pl-4 pb-4">
          <p className="text-xs font-normal text-secondary-n-50">Settings</p>
        </div>
        <Separator className="bg-secondary-n-900 m-0!" />
        <div className="flex flex-col gap-1 p-2">
          {memoizedTabs.map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <Button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                role="tab"
                aria-selected={isActive}
                aria-label={`${tab.name} settings tab`}
                variant="ghost"
                className={cn(
                  "flex justify-between items-center p-2 rounded-md hover:bg-secondary-n-950 h-7 overflow-hidden whitespace-nowrap w-full px-2 py-1 transition-all text-left",
                  isActive && "bg-secondary-n-900",
                )}
              >
                <div className="flex gap-2 items-center">
                  <p
                    className={cn(
                      "text-xs text-left w-44 overflow-hidden text-ellipsis",
                      isActive ? "text-secondary-n-50" : "text-secondary-n-400",
                    )}
                  >
                    {tab.name}
                  </p>
                </div>
              </Button>
            );
          })}
        </div>
      </div>
    );
  },
);

ProjectConfigNav.displayName = "ProjectConfigNav";

export default ProjectConfigNav;
